/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { ClassAttributes, HTMLAttributes, useState } from 'react';
import { GetSingleSelectCustomField } from '../../../typings';
import { customFieldsAtom } from '../../../states/custom-fields';
import { useAtomValue } from 'jotai';
import Loader from '../../../../../../shared/components/molecules/loader';
import DTDropdown from '../../../../../../shared/components/molecules/custom-dropdown';
import { QSTypography } from '../../../../../../shared/components/atoms';
import { BORDER_COLOR } from '../../../../../../shared/typings';
import { QSTextInput } from '../../../../../../shared/components/molecules';
import { Box, ButtonBase, Icon } from '@mui/material';
import { Search } from '@mui/icons-material';
import { Virtuoso } from 'react-virtuoso';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import CheckIcon from '@mui/icons-material/Check';
import { ReactComponent as FilterIcon } from '../../../../../../shared/assets/svg/filter.svg';
import FilterSettingIcon from '../../../../../../shared/icons/icons-revamp/filter_setting';

interface CustomListDropdownProps {
  setSelectedListField: (field: GetSingleSelectCustomField) => any;
  selectedListField?: GetSingleSelectCustomField;
  containerProps?: ClassAttributes<HTMLDivElement> & HTMLAttributes<HTMLDivElement>;
}

const CustomListDropdown = ({ selectedListField, setSelectedListField, containerProps }: CustomListDropdownProps) => {
  const [dropdown, setDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const { data, loading, error } = useAtomValue(customFieldsAtom);
  const singleSelectFields = (data?.customFields ?? []).filter((field) => field.type === 'single_select') || [];
  const filteredSingleSelectFields = singleSelectFields?.filter((filed) =>
    filed.name.toLocaleLowerCase().includes(searchTerm?.toLocaleLowerCase())
  );
  const selectedListName = selectedListField ? selectedListField.name : 'Select list field';
  if (loading) {
    return (
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: center;
          max-height: 100%;
        `}
      >
        <Loader size={20} />
      </div>
    );
  }
  return (
    <DTDropdown
      trigger={
        <ButtonBase
          css={css`
            display: flex;
            padding: 4px 8px;
            align-items: center;
            background: '#fff';
            border-radius: 0px;
            border-top-right-radius: 4px;
            border-bottom-right-radius: 4px;
            height: 100%;
          `}
        >
          <FilterSettingIcon size={20} />
        </ButtonBase>
      }
      offsetModifier={[-100, 4]}
      open={dropdown}
      setOpen={setDropdown}
      position="bottom"
      customWidth="max-content"
      dropdownWidthAsPopperMinWidth
      outerContainerCss={css`
        border: none;
        border-radius: 0px;
        border-top-right-radius: 4px;
        border-bottom-right-radius: 4px;
        height: 100%;
      `}
      triggerContainerCss={css`
        height: 100%;
      `}
      containerProps={{
        ...containerProps,
        style: {
          background: dropdown ? 'linear-gradient(0deg, #EAF3F0 0%, #EAF3F0 100%), #FFF' : '#fff',
          width: 'unset',
          borderTopRightRadius: 7,
          borderBottomRightRadius: 7,
          borderLeft: '1px solid #e0e0e9',
        },
      }}
      activeBorderColor={BORDER_COLOR}
      hoverBorderColor={BORDER_COLOR}
    >
      <div
        css={[
          css`
            background: #fff;
            z-index: 10;
            display: flex;
            flex-direction: column;
            box-shadow: 0 0 8px 0 rgba(0, 0, 0, 0.13);
            border-radius: 4px;
            height: 300px;
          `,
        ]}
      >
        <QSTextInput
          sx={{
            width: '100%',
            padding: '8px',
            borderBottom: '1px solid #e0e0e9',
          }}
          onChange={(e) => setSearchTerm(e.target.value)}
          defaultValue={searchTerm}
          placeholder="Search"
          textInputProps={{
            sx: {
              background: 'white',
              border: `1px solid ${BORDER_COLOR}`,
              borderRadius: '6px',
              minHeight: '35px',
              justifyContent: 'center',
              padding: '0 8px',
              '.MuiInputBase-root::before': {
                borderBottom: 'none !important',
              },
              '.MuiInputBase-root,.MuiInput-root': {
                '&::before': {
                  border: 'none',
                },
                '&::after': {
                  border: 'none',
                },
                '&:hover:not(.Mui-disabled)::before': {
                  border: 'none',
                },
                'input::placeholder, textArea::placeholder': {
                  color: '#7B7B7B',
                },
              },
            },
            InputProps: {
              startAdornment: (
                <Icon
                  css={css`
                    display: flex;
                    align-items: center;
                  `}
                >
                  <Search
                    css={css`
                      width: 20px;
                      height: 20px;
                    `}
                  />
                </Icon>
              ),
            },
            className: 'nofocus',
            autoFocus: true,
            autoComplete: 'off',
            onKeyDown: (e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
              }
            },
          }}
        />
        <Virtuoso
          totalCount={(filteredSingleSelectFields ?? []).length}
          data={filteredSingleSelectFields ?? []}
          css={css`
            scrollbar-width: thin;
            scrollbar-color: rgba(11, 20, 26, 0.4) transparent;
            &::-webkit-scrollbar {
              width: 10px;
            }
            &::-webkit-scrollbar-track {
              background: transparent;
            }
            &::-webkit-scrollbar-thumb {
              background: rgba(11, 20, 26, 0.4);
              border-radius: 5px;
            }
            &::-webkit-scrollbar-thumb:hover {
              background: transparent;
            }
            border-radius: 4px;
          `}
          itemContent={(index, list) => (
            <ButtonBase<'div'>
              css={css`
                display: flex;
                align-items: center;
                align-self: stretch;
                gap: 12px;
                width: 100%;
                padding: 8px 16px;
                cursor: pointer;
                min-height: 40px;
                height: max-content;
                background: ${list.id === selectedListField?.id ? '#eaf3f0' : '#ffffff'};
                border-bottom: ${index === (filteredSingleSelectFields ?? []).length - 1
                  ? 'none'
                  : '1px solid #e0e0e9'};
                &:hover {
                  background-color: ${list.id === selectedListField?.id ? 'rgba(93, 183, 121, 0.10)' : '#f5f5f5'};
                }
              `}
              onClick={() => {
                setSelectedListField(list as GetSingleSelectCustomField);
                setDropdown(false);
              }}
              component="div"
            >
              <Box
                css={css`
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  flex: 1 0 0;
                `}
              >
                <QSTypography>{list.name}</QSTypography>
                {list.id === selectedListField?.id ? (
                  <CheckIcon
                    css={css`
                      font-size: 16px;
                      width: 16px;
                      height: 16px;
                      color: #3f906d;
                    `}
                  />
                ) : null}
              </Box>
            </ButtonBase>
          )}
          components={{
            EmptyPlaceholder: () => {
              return (
                <div
                  css={css`
                    height: 100%;
                    width: 100%;
                    display: flex;
                    flex-direction: column;
                    column-gap: 8px;
                    justify-content: center;
                    align-items: center;
                    padding: 16px;
                    color: #8484a8;
                  `}
                >
                  <Icon>
                    <ReportProblemIcon />
                  </Icon>
                  <QSTypography
                    css={css`
                      margin: 0px;
                      font-family: Roboto, sans-serif;
                      font-weight: 400;
                      font-size: 1rem;
                      line-height: 1.5;
                      text-align: center;
                    `}
                  >
                    No list fields found
                  </QSTypography>
                </div>
              );
            },
          }}
        />
      </div>
    </DTDropdown>
  );
};
export default CustomListDropdown;
